import React, { Component } from 'react';
import {
    ArrayInput,
    BooleanInput,
    FormDataConsumer,
    GET_LIST,
    required,
    SelectInput,
    SimpleFormIterator,
    withDataProvider,
} from 'react-admin';
import { DateTimeInput } from 'react-admin-date-inputs';
import { isAdmin } from '../utils';

const AVAILABLE_PARTNER_SERVICES_RESOURCE = 'partner_contracts/partner_services';

const partnerServiceOptionText = choice =>
    choice && choice.id != null ? `${choice.name || 'Сервисный партнер'} (${choice.id})` : '';

const validateUniquePartnerServices = values => {
    if (!Array.isArray(values)) {
        return undefined;
    }

    const ids = values
        .map(value => Number(value && value.partner_service_id))
        .filter(Number.isInteger);

    return ids.length === new Set(ids).size
        ? undefined
        : 'Нельзя выбрать несколько раз одного сервисного партнера';
};

const mergePartnerServices = (...collections) => {
    const byId = new Map();

    collections
        .filter(Array.isArray)
        .reduce((result, collection) => result.concat(collection), [])
        .forEach(partnerService => {
            if (!partnerService || partnerService.id == null) {
                return;
            }
            byId.set(Number(partnerService.id), {
                ...byId.get(Number(partnerService.id)),
                ...partnerService,
                id: Number(partnerService.id),
            });
        });

    return Array.from(byId.values()).sort((left, right) =>
        String(left.name || '').localeCompare(String(right.name || ''))
    );
};

const getSelectedPartnerServices = record =>
    ((record && record.visible_partner_services) || []).map(rule => ({
        id: Number(rule.partner_service_id),
        name:
            rule.partner_service && rule.partner_service.name
                ? rule.partner_service.name
                : `Сервисный партнер ${rule.partner_service_id}`,
    }));

const getUnselectedPartnerServices = (
    partnerServices,
    visiblePartnerServices,
    currentPartnerServiceId
) => {
    const currentId = Number(currentPartnerServiceId);
    const selectedIds = new Set(
        (visiblePartnerServices || [])
            .map(rule => Number(rule && rule.partner_service_id))
            .filter(Number.isInteger)
    );

    return partnerServices.filter(({ id }) => {
        const partnerServiceId = Number(id);

        return partnerServiceId === currentId || !selectedIds.has(partnerServiceId);
    });
};

class SessionVisibilityInput extends Component {
    state = {
        availablePartnerServices: [],
    };

    componentDidMount() {
        this.loadPartnerServices();
    }

    componentDidUpdate(previousProps) {
        const previousRecord = previousProps.record || {};
        const record = this.props.record || {};

        if (
            previousRecord.id !== record.id ||
            previousRecord.owner_partner_service_id !== record.owner_partner_service_id ||
            previousProps.permissions !== this.props.permissions
        ) {
            this.loadPartnerServices();
        }
    }

    componentWillUnmount() {
        this.unmounted = true;
    }

    loadPartnerServices = async () => {
        const { dataProvider, permissions, record = {} } = this.props;

        if (!record.id) {
            return;
        }

        const requestParams = {
            pagination: { page: 1, perPage: 1000 },
            sort: { field: 'name', order: 'ASC' },
            filter: isAdmin(permissions) ? { partner_contract_id: record.id } : {},
        };
        try {
            const availableResult = await dataProvider(
                GET_LIST,
                AVAILABLE_PARTNER_SERVICES_RESOURCE,
                requestParams
            );

            if (!this.unmounted) {
                this.setState({
                    availablePartnerServices: availableResult.data || [],
                });
            }
        } catch (error) {
            if (!this.unmounted) {
                this.setState({
                    availablePartnerServices: [],
                });
            }
        }
    };

    render() {
        const { classes, record = {} } = this.props;
        const choices = mergePartnerServices(
            this.state.availablePartnerServices,
            getSelectedPartnerServices(record)
        );

        return (
            <>
                <BooleanInput
                    label="Показывать сессии всех витрин"
                    source="legacy_session_visibility"
                    defaultValue
                    className={classes && classes.wide}
                />
                <FormDataConsumer>
                    {({ formData }) =>
                        formData.legacy_session_visibility === false ? (
                            <ArrayInput
                                label="Доступные витрины"
                                source="visible_partner_services"
                                validate={validateUniquePartnerServices}
                                className={classes && classes.wide}
                            >
                                <SimpleFormIterator>
                                    <FormDataConsumer>
                                        {({ formData, getSource, scopedFormData, ...rest }) => (
                                            <SelectInput
                                                {...rest}
                                                label="Сервисный партнер"
                                                source={getSource('partner_service_id')}
                                                choices={getUnselectedPartnerServices(
                                                    choices,
                                                    formData && formData.visible_partner_services,
                                                    scopedFormData &&
                                                        scopedFormData.partner_service_id
                                                )}
                                                optionText={partnerServiceOptionText}
                                                validate={required()}
                                                className={classes && classes.wide}
                                            />
                                        )}
                                    </FormDataConsumer>
                                    <FormDataConsumer>
                                        {({ getSource, ...rest }) => (
                                            <DateTimeInput
                                                {...rest}
                                                label="Показывать сессии с"
                                                source={getSource('sessions_visible_from')}
                                                options={{
                                                    format: 'dd.MM.YYYY HH:mm',
                                                    ampm: false,
                                                    clearable: true,
                                                }}
                                                className={classes && classes.wide}
                                            />
                                        )}
                                    </FormDataConsumer>
                                </SimpleFormIterator>
                            </ArrayInput>
                        ) : null
                    }
                </FormDataConsumer>
            </>
        );
    }
}

export const PartnerContractSessionVisibilityInput = withDataProvider(SessionVisibilityInput);
